/**
 * What a connected payroll system exchanges with Timefly (#296 ONB-18, ONB-19): each direction, who owns
 * the data, whether sending is on, and what became of the last sends. The rows are what the code does
 * (backend/services/tripletex.js, poweroffice.js; docs/integrations/), not a promise; anything not tried
 * against a real company says so. Viewing it moves nothing: sending changes only on the switch, which
 * the server keeps (PUT {base}/outbound, administrator only).
 */
import { errorText, put } from '@/api/client';
import { T } from '@/i18n';
import { useTenant } from '@/shell/tenant';
import { Chip, Switch, toast } from '@/ui';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { when } from './Month';
import type { PayrollSystem } from './payrollSystem';
import './lonn.css';

/** A send as the status lists it; Tripletex carries payslips, PowerOffice lines and a reference */
export interface SentRow {
  year: number;
  month: number;
  outcome?: 'sent' | 'unknown' | 'partial';
  lines: number;
  payslips?: number;
  sent_at: string;
}
interface Status {
  outbound_on?: boolean;
  outbound_at?: string | null;
  sent: SentRow[];
}

type Row = { dir: 'in' | 'out'; what: string; how: string; owner: string };

/** One list per system, from what its service reads and writes today. */
function rows(sys: PayrollSystem, name: string): Row[] {
  const out: Row[] = [
    {
      dir: 'out',
      what: T('Godkjente timer for en måned'),
      how:
        sys.key === 'tripletex'
          ? T('Som ett lønnsbilag når du trykker Send under Rapporter.')
          : T('Som lønnslinjer når du trykker Send under Rapporter.'),
      owner: T('Timefly eier vakter, stemplinger og godkjenning'),
    },
  ];
  if (sys.key === 'poweroffice')
    out.push({
      dir: 'out',
      what: T('Lønnsplanen fra Vaktplan'),
      how: T('Som Timeflys eget budsjett i {name}, når du trykker Send. Andre budsjetter endres ikke.', {
        name,
      }),
      owner: T('Timefly eier planen'),
    });
  return [
    {
      dir: 'in',
      what: T('Ansatte og ansattnummer'),
      how: T('Du velger hvem som kobles eller legges til. Ingen blir invitert.'),
      owner: T('{name} eier ansattnummeret', { name }),
    },
    {
      dir: 'in',
      what: T('Lønn og kontrakt'),
      how: T('Vises som forskjeller. Bare det du krysser av, skrives i Timefly.'),
      owner: T('{name} eier lønn og kontrakt', { name }),
    },
    {
      dir: 'in',
      what: T('Lønnsarter'),
      how: T('Nummeret kobles til lønnsarten i Timefly.'),
      owner: T('{name} eier lønnsartene', { name }),
    },
    {
      dir: 'in',
      what: T('Nye og sluttede ansatte'),
      how: T('Listes til gjennomgang. En sluttdato settes først når du krysser av.'),
      owner: T('{name} eier ansettelsen', { name }),
    },
    {
      dir: 'in',
      what: T('Permisjoner'),
      how: T('Vises bare. Ingenting skrives i Timefly.'),
      owner: name,
    },
    {
      dir: 'in',
      what: T('Omsetning og lønnskostnad fra regnskapet'),
      how: T('Faktiske tall. Dagene du velger, lagres som omsetning i Timefly.'),
      owner: T('Regnskapet i {name}', { name }),
    },
    {
      dir: 'in',
      what: T('Budsjett'),
      how: T('Vises som mål, aldri som omsetning. Prognosen i Timefly er sin egen.'),
      owner: T('Regnskapet i {name}', { name }),
    },
    ...out,
  ];
}

/** «Sendt», «Uvisst» or «Delvis», with what it means for sending again. */
export function sentState(x: SentRow): { tone: 'ok' | 'warn'; label: string; open: boolean } {
  if (x.outcome === 'unknown') return { tone: 'warn', label: T('Uvisst om det kom fram'), open: true };
  if (x.outcome === 'partial')
    return { tone: 'warn', label: T('Delvis: {n} linjer ligger igjen', { n: x.lines }), open: true };
  return {
    tone: 'ok',
    label:
      x.payslips != null
        ? T('Sendt, {n} lønnsslipper', { n: x.payslips })
        : T('Sendt, {n} lønnslinjer', { n: x.lines }),
    open: false,
  };
}

const mm = (y: number, m: number) => `${String(m).padStart(2, '0')}.${y}`;

export function IntegrationCapability({
  sys,
  name,
  status,
  queryKey,
}: { sys: PayrollSystem; name: string; status: Status; queryKey: unknown[] }) {
  const { tenant } = useTenant();
  const qc = useQueryClient();
  const admin = tenant?.me?.is_admin === true;
  const on = status.outbound_on === true;
  const flip = useMutation({
    mutationFn: (next: boolean) => put<Status>(`${sys.base}/outbound`, { on: next }),
    onSuccess: (s) => {
      qc.setQueryData(queryKey, s);
      toast(
        s.outbound_on
          ? T('Sending til {name} er slått på', { name })
          : T('Sending til {name} er slått av', { name }),
      );
    },
    onError: (e) => toast(errorText(e)),
  });
  const recent = status.sent.slice(0, 3);
  return (
    <div className="icap" data-testid={`capability-${sys.key}`}>
      <h3>{T('Hva som går hvor')}</h3>
      <ul className="icapl">
        {rows(sys, name).map((r) => (
          <li key={`${r.dir}-${r.what}`} data-dir={r.dir}>
            <span className="icapd">{r.dir === 'in' ? T('Inn til Timefly') : T('Ut fra Timefly')}</span>
            <span>
              <b>{r.what}</b> {r.how}
            </span>
            <span className="icapo">{r.owner}</span>
          </li>
        ))}
      </ul>
      {sys.key === 'poweroffice' && (
        <p className="icapn" data-testid="capability-unverified">
          {/* docs/integrations/poweroffice.md: production keys are [U] until the first company connects */}
          {T('Prøvd mot demoen til PowerOffice, ikke mot en ekte bedrift ennå.')}
        </p>
      )}
      <div className="icapsw">
        <Switch
          checked={on}
          disabled={!admin || flip.isPending}
          onChange={(next) => flip.mutate(next)}
          name={T('Sending til {name}', { name })}
          label={on ? T('Sending er på') : T('Sending er av')}
        />
        <span>
          {on
            ? T('Godkjente timer går til {name} bare når noen trykker Send.', { name })
            : T(
                'Ingenting sendes til {name} før en administrator slår sending på. Å hente og se går fortsatt.',
                {
                  name,
                },
              )}
          {status.outbound_at ? ` ${T('Endret {when}.', { when: when(status.outbound_at) })}` : ''}
        </span>
      </div>
      <h3>{T('Siste sendinger')}</h3>
      {recent.length === 0 ? (
        <p className="icapn">{T('Ingenting er sendt ennå.')}</p>
      ) : (
        <ul className="icapl" data-testid="capability-sent">
          {recent.map((x) => {
            const st = sentState(x);
            return (
              <li key={`${x.year}-${x.month}`}>
                <span className="icapd">{mm(x.year, x.month)}</span>
                <span>
                  <Chip tone={st.tone} small>
                    {st.label}
                  </Chip>{' '}
                  {when(x.sent_at)}
                </span>
                {st.open ? (
                  <Link className="mlink" to="/lonn?vis=eksport">
                    {T('Avklar før måneden sendes på nytt')}
                  </Link>
                ) : (
                  <span />
                )}
              </li>
            );
          })}
        </ul>
      )}
      <h3>{T('Ikke fra {name} ennå', { name })}</h3>
      <p className="icapn" data-testid="capability-not-yet">
        {T(
          'Ferie- og fraværssaldo, lønnskalender og frister, bemanningsbehov og endringer i stilling med virkningsdato hentes ikke. Før dem inn i Timefly selv, på ansattkortet og under Innstillinger.',
        )}
      </p>
    </div>
  );
}
